'use client';

import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { Link } from '@/components/Link';
import { MAGAZINE_LABEL_CLASS_NAME } from '@/components/magazine/magazineLabelClassName';
import type { MagazineArticleTeaser, MagazineAuthor } from '@/lib/api';
import { AUTHOR_ANCHOR } from '@/lib/magazine/extractTableOfContents';
import { useMagazineHref } from '@/lib/magazine/useMagazineHref';
import { buildImageUrl } from '@/lib/render/imageUrl';
import { RichText } from '@/lib/render/RichText';
import { toBlocksContent } from '@/lib/render/toBlocksContent';

export enum AuthorBoxVariant {
  /** Byline above the article body. */
  Chip = 'chip',
  /** The box under the article body, with the author's recent articles. */
  Article = 'article',
  /** Full-width biography at the top of the author page. */
  Profile = 'profile',
}

interface AuthorBoxProps {
  author: MagazineAuthor;
  variant: AuthorBoxVariant;
  /** The author's five most recent *other* articles; `article` variant only. */
  recentArticles?: MagazineArticleTeaser[];
  /**
   * The element the author's name renders as. Passed in rather than derived
   * from the variant: the profile variant's block is on the shared Page
   * palette, so an editor can put it on the article template, where the page's
   * `<h1>` is already the article title. Only the caller knows which page it is
   * rendering on (10.02.06 § AC 3).
   */
  nameHeading?: 'h1' | 'h3';
}

/**
 * The author's three surfaces, one component. The chip is route-rendered chrome
 * in `MagazineArticleHeader`; the article variant is rendered by
 * `magazine.author-box` and the profile variant by `magazine.author-page-header`.
 *
 * `gender` is a required, closed enumeration (30.02.08 § `gender`) and the DTO
 * types it as one, so the four label sets are indexed directly on it — there is
 * no neutral copy and no fallback here: a malformed row surfaces in the backend
 * projection rather than silently misgendering the author.
 *
 * Only the `chip` set carries a `{name}` token; the other three read as headings
 * in both locales, so their calls pass no values.
 */
export const AuthorBox = ({
  author,
  variant,
  recentArticles = [],
  nameHeading = 'h3',
}: AuthorBoxProps) => {
  const t = useTranslations('magazine');
  const href = useMagazineHref();
  const gender = author.gender;
  const name = author.name;
  const portraitSrc = author.image ? buildImageUrl(author.image) : '';

  if (variant === AuthorBoxVariant.Chip) {
    return (
      <div className="bg-secondary-lighter mx-auto flex w-fit items-center gap-4 rounded-2xl px-6 py-4 text-left">
        {portraitSrc && (
          <Portrait
            src={portraitSrc}
            alt={name}
            size={56}
            sizes="56px"
            className="shrink-0 self-start"
          />
        )}
        <div className="flex flex-col gap-1">
          <span className="text-lg font-semibold">
            {t(`author.chip.${gender}`, { name })}
          </span>
          {/* An in-page anchor, not a link to the author page: it takes a
              reader who wants the background down to the box below the body. */}
          <a href={`#${AUTHOR_ANCHOR}`} className="text-primary text-base">
            {t(`author.chipLink.${gender}`)}
          </a>
        </div>
      </div>
    );
  }

  const description = toBlocksContent(author.description);
  // Only the `article` variant links to the profile, so only it pays for the
  // lookup — `href` is the value returned by the hook above, not a hook itself,
  // so calling it conditionally is fine.
  //
  // `undefined` wherever the brand and locale serve no author page — CH in
  // French. The button is then omitted rather than pointed at the German
  // profile (product-owner decision), which is exactly the same derivation
  // that drops `article:author` and the JSON-LD `author.url`.
  const profileHref =
    variant === AuthorBoxVariant.Article
      ? href('author', author.slug)
      : undefined;

  return (
    <section
      {...(variant === AuthorBoxVariant.Article ? { id: AUTHOR_ANCHOR } : {})}
      className={classNames(
        'w-full',
        // The chip's `#autor` jump would otherwise land the box's top edge
        // under the sticky header.
        variant === AuthorBoxVariant.Article && 'scroll-mt-24',
        variant === AuthorBoxVariant.Profile
          ? ''
          : 'bg-secondary-lighter rounded-2xl p-6',
      )}
    >
      {/* A `<p>`, not a heading: it labels the section, and the author's name
          below it is the author page's `<h1>` — a heading here would precede
          that `<h1>` and invert the outline (10.02.06 § AC 3). */}
      {variant === AuthorBoxVariant.Profile && (
        <p className="mb-8 text-center text-2xl">
          {t(`author.profileHeading.${gender}`)}
        </p>
      )}

      <div
        className={classNames(
          'flex flex-col gap-6 md:flex-row',
          // Stacked, the portrait and the name read as a centred masthead;
          // from `md` the row layout takes over and both go back to the left.
          variant === AuthorBoxVariant.Profile &&
            'mx-auto max-w-4xl items-center md:items-start',
        )}
      >
        {portraitSrc && (
          <Portrait
            src={portraitSrc}
            alt={name}
            size={variant === AuthorBoxVariant.Profile ? 180 : 120}
            sizes={variant === AuthorBoxVariant.Profile ? '180px' : '120px'}
            className={classNames(
              'shrink-0',
              variant === AuthorBoxVariant.Profile
                ? 'self-center md:self-start'
                : 'self-start',
            )}
          />
        )}
        <div className="flex flex-col gap-2">
          {/* `h3` unless the caller says otherwise: the author page has no
              other heading to carry its `<h1>`, every other page already has
              one (10.02.06 § AC 3). */}
          <NameHeading
            as={nameHeading}
            className={classNames(
              'text-xl font-semibold',
              variant === AuthorBoxVariant.Profile &&
                'text-center md:text-left',
            )}
          >
            {name}
          </NameHeading>
          {author.subtitle && (
            <p
              className={classNames(
                'text-muted text-sm tracking-[0.05em] uppercase',
                variant === AuthorBoxVariant.Profile &&
                  'text-center md:text-left',
              )}
            >
              {author.subtitle}
            </p>
          )}
          {description && <RichText nodes={description} />}
          {/* `profileHref` is `undefined` on the profile variant: the reader is
              already there, and the full grid follows below. */}
          {profileHref && (
            <Link
              href={profileHref}
              className="border-primary text-primary mt-2 w-fit rounded-full border px-6 py-2 font-bold"
            >
              {t('author.profileLink')}
            </Link>
          )}
        </div>
      </div>

      {variant === AuthorBoxVariant.Article && recentArticles.length > 0 && (
        <div className="mt-6">
          <h4 className="mb-2 font-semibold">
            {t(`author.recentArticlesHeading.${gender}`)}
          </h4>
          {/* Plain text links, not teaser cards, styled as the same clickable
              label as the teaser card's `Weiterlesen »`. An article the map
              serves no path for on this brand and locale is left out rather
              than linked to a URL that 404s — the rule the category pills
              follow. */}
          <ul className="flex list-none flex-col gap-1 pl-0">
            {recentArticles.map((article) => {
              const articleHref = href('article', article.slug);
              return articleHref ? (
                <li key={article.documentId}>
                  <Link
                    href={articleHref}
                    className={MAGAZINE_LABEL_CLASS_NAME}
                  >
                    {article.title}
                  </Link>
                </li>
              ) : null;
            })}
          </ul>
        </div>
      )}
    </section>
  );
};

/** The name's element differs per page; everything else about it does not. */
const NameHeading = ({
  as: Tag,
  className,
  children,
}: {
  as: 'h1' | 'h3';
  className?: string;
  children: ReactNode;
}) => <Tag className={className}>{children}</Tag>;

/**
 * Fixed aspect ratio, cropped to a circle rather than distorted (AC 6).
 *
 * Cross-axis alignment comes from the caller (`self-start`, or `self-center`
 * while the profile is stacked) and is load-bearing: the portrait is a flex
 * item, and the row's default `align-items: stretch` overrides
 * `aspect-square`'s height, turning the circle into an ellipse next to a long
 * biography. `h-auto` hands the height to `aspect-square` rather than to the
 * `height` attribute, which is what `next/image` asks for whenever CSS drives
 * one of the two dimensions.
 */
const Portrait = ({
  src,
  alt,
  size,
  sizes,
  className,
}: {
  src: string;
  alt: string;
  size: number;
  sizes: string;
  className?: string;
}) => (
  <Image
    src={src}
    alt={alt}
    width={size}
    height={size}
    sizes={sizes}
    className={`aspect-square h-auto rounded-full object-cover ${className ?? ''}`}
  />
);
